/* eslint-disable no-unused-vars */
import { FC, useState } from 'react';
import {
    Menu,
    Modal,
    notification,
    Spin,
    Typography,
} from 'antd';
import {
    FaRegTrashAlt,
    FaEdit,
    FaPaperPlane,
    FaExchangeAlt,
} from 'react-icons/fa';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { BsLightbulbFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IDriver } from '../../interfaces/driver.interface';
import { removeDriver, resendInvitation, updateDriver } from '../../api/drivers.api';
import { useWorkspaceStore } from '../../store/workspace.store';
import { checkDriverStatus } from '../../utils/driver.util';
import { useDriversContext } from './context/drivers.ctx';

interface MenuDriverProps {
    driver: IDriver;
}

const { confirm } = Modal;
const { Text } = Typography;

const MenuDriver: FC<MenuDriverProps> = ({ driver }) => {
    const { t: td } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });
    const { t: ta } = useTranslation('shared', { keyPrefix: 'ACTIONS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const { fetchData, handleSetDriver } = useDriversContext();

    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();

    const resendDriverInvitation = () => {
        setLoading(true);
        resendInvitation(driver._id)
            .then(() => {
                notification.success({
                    message: td('RESEND_INVITATION'),
                    description: td('RESEND_INVITATION_SUCCESS'),
                });
            })
            .catch((error: any) => {
                const description = error.response.data.message;
                notification.error({
                    message: td('SEND_INVITATION_ERROR'),
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const handleRemoveDriver = () => {
        confirm({
            width: 500,
            title: td('DELETE_QUESTION'),
            content: `${driver.name} ${driver.lastName}`,
            okText: ta('DELETE'),
            cancelText: ta('CANCEL'),
            onOk() {
                setLoading(true);
                const payload = {
                    userID: driver._id,
                    smallFleetID: workspace?._id || '',
                };
                removeDriver(payload)
                    .then(() => {
                        notification.success({
                            message: td('DRIVER_REMOVED'),
                        });
                        fetchData();
                    }).catch((error: any) => {
                        const description = error.response.data.message;
                        notification.error({
                            message: td('DRIVER_NO_REMOVED'),
                            description,
                        });
                    }).finally(() => setLoading(false));
            },
        });
    };

    const handleChangeStatus = () => {
        const status = checkDriverStatus(driver.isBusy, driver.onDutyFlag);
        const driverName = `${driver.name} ${driver.lastName}`;

        const newStatus = status !== 'ONLINE';

        confirm({
            title: td('CHANGE_STATUS_QUESTION_DESCRIPTION', { driverName }),
            width: 600,
            content: (
                status === 'ONLINE' && (
                    <div className="help">
                        <div className="help__tag">
                            <BsLightbulbFill />
                            {td('IMPORTANT')}
                        </div>
                        <Text>
                            {td('OFF_DUTY_CHANGE_STATUS_DESCRIPTION')}
                        </Text>
                    </div>
                )
            ),
            icon: <FaExchangeAlt size={16} color="#6C8EEF" />,
            okText: td('CHANGE_STATUS'),
            okType: 'primary',
            onOk() {
                setLoading(true);
                updateDriver(driver._id, { onDutyFlag: newStatus })
                    .then(() => {
                        notification.success({
                            message: td('CHANGE_STATUS'),
                            description: td('CHANGE_STATUS_SUCCESS'),
                        });

                        handleSetDriver(driver._id, { onDutyFlag: newStatus });
                    })
                    .catch((error: any) => {
                        const description = error.response.data.message;
                        notification.error({
                            message: 'Error',
                            description,
                        });
                    })
                    .finally(() => setLoading(false));
            },
            cancelText: ta('CANCEL'),
            onCancel() {
                console.log('Cancel');
            },
        });
    };

    const items: ItemType[] = [
        {
            key: 'EDIT',
            label: ta('EDIT'),
            icon: loading ? <Spin size="small" /> : <FaEdit />,
            onClick: () => navigate(`/drivers/${driver._id}`),
        },
        {
            key: 'CHANGE_STATUS',
            label: td('CHANGE_STATUS'),
            icon: loading ? <Spin size="small" /> : <FaExchangeAlt />,
            onClick: () => handleChangeStatus(),
        },
        {
            key: 'RESEND_INVITATION',
            label: td('RESEND_INVITATION'),
            icon: loading ? <Spin size="small" /> : <FaPaperPlane />,
            onClick: () => resendDriverInvitation(),
        },
        {
            key: 'DELETE',
            label: ta('DELETE'),
            icon: loading ? <Spin size="small" /> : <FaRegTrashAlt />,
            danger: true,
            onClick: () => handleRemoveDriver(),
        },
    ];
    return (
        <Menu items={items} />
    );
};

export { MenuDriver };
